// =====================================================================
// boardService (Quadros Q1 / S59) — chamadas axios pra API /boards/*.
// Todos exigem isAuth no backend; multi-tenant por companyId do JWT.
// =====================================================================
import api from "./api";

// ------------------------------ Boards -------------------------------
export const listBoards = async (includeArchived = false) => {
  const params = {};
  if (includeArchived) params.archived = true;
  const { data } = await api.get("/boards", { params });
  return data; // { boards }
};
export const getBoard = async (id, includeArchivedCards = false) =>
  (await api.get(`/boards/${id}`, { params: includeArchivedCards ? { archivedCards: true } : {} })).data; // board + lists + cards
export const createBoard = async (payload) => (await api.post("/boards", payload)).data;
export const updateBoard = async (id, payload) => (await api.put(`/boards/${id}`, payload)).data;
export const archiveBoard = async (id) => (await api.post(`/boards/${id}/archive`)).data;
export const restoreBoard = async (id) => (await api.post(`/boards/${id}/restore`)).data;
export const removeBoard = async (id) => (await api.delete(`/boards/${id}`)).data;
export const uploadBoardBackground = async (id, file) => {
  const fd = new FormData();
  fd.append("file", file);
  return (await api.post(`/boards/${id}/background`, fd, { headers: { "Content-Type": "multipart/form-data" } })).data;
};

// ------------------------------ Lists --------------------------------
export const createList = async (boardId, payload) => (await api.post(`/boards/${boardId}/lists`, payload)).data;
export const reorderLists = async (boardId, orderedIds) => (await api.post(`/boards/${boardId}/lists/reorder`, { orderedIds })).data;
export const updateList = async (id, payload) => (await api.put(`/board-lists/${id}`, payload)).data;
export const removeList = async (id) => (await api.delete(`/board-lists/${id}`)).data;

// ------------------------------ Cards --------------------------------
export const createCard = async (listId, payload) => (await api.post(`/board-lists/${listId}/cards`, payload)).data;
export const updateCard = async (id, payload) => (await api.put(`/board-cards/${id}`, payload)).data;
export const moveCard = async (id, payload) => (await api.post(`/board-cards/${id}/move`, payload)).data; // { toListId, toIndex }
export const removeCard = async (id) => (await api.delete(`/board-cards/${id}`)).data;
export const cardActivity = async (id) => (await api.get(`/board-cards/${id}/activity`)).data; // { activity }
export const addCardComment = async (id, body) => (await api.post(`/board-cards/${id}/comment`, { body })).data;

// --------------------------- Vínculos CRM (Q2) ---------------------------
// search: { kind, items:[{ id, label, sub }] }
export const searchCrmLinks = async (kind, term) =>
  (await api.get("/board-link/search", { params: { kind, term: term || "", limit: 10 } })).data;
// resolve: { cardId, links:{ patient?, ticket?, ... } } cada = { id, label, sub, route|null }
export const getCardLinks = async (cardId) => (await api.get(`/board-cards/${cardId}/links`)).data;
export const setCardLinks = async (cardId, links) => (await api.put(`/board-cards/${cardId}/links`, { links })).data;
// "virar tarefa" (Q2b): { ticketId, listId, title } -> card pré-linkado
export const createCardFromTicket = async (payload) => (await api.post("/board-link/from-ticket", payload)).data;

// tipos linkáveis (kind -> coluna do card é `${kind}Id`)
export const LINK_KINDS = [
  { kind: "patient", label: "Paciente", icon: "👤" },
  { kind: "ticket", label: "Conversa", icon: "💬" },
  { kind: "appointment", label: "Agendamento", icon: "📅" },
  { kind: "quote", label: "Orçamento", icon: "💰" },
  { kind: "lead", label: "Lead", icon: "🎯" }
];
export const linkKindMeta = (kind) => LINK_KINDS.find((k) => k.kind === kind) || { kind, label: kind, icon: "🔗" };

// ------------------------------ Helpers ------------------------------
export const BOARD_TYPES = [
  { value: "task", label: "Tarefas / Projetos" },
  { value: "pipeline", label: "Pipeline de pacientes/leads" }
];

export const LABEL_COLORS = [
  "#c62828", "#ad1457", "#6a1b9a", "#1565c0", "#2e7d32", "#f9a825", "#ef6c00", "#4e342e", "#37474f"
];

export const fmtDateBR = (iso) => {
  if (!iso) return "";
  const [y, m, d] = String(iso).slice(0, 10).split("-");
  if (!y || !m || !d) return "";
  return `${d}/${m}/${y}`;
};

export const boardErrorMessage = (err) => {
  const code = err && err.response && err.response.data && err.response.data.error;
  const map = {
    ERR_BOARD_NOT_FOUND: "Quadro não encontrado.",
    ERR_BOARD_NAME_REQUIRED: "Dê um nome ao quadro.",
    ERR_BOARD_LIST_NOT_FOUND: "Coluna não encontrada.",
    ERR_BOARD_CARD_NOT_FOUND: "Card não encontrado.",
    ERR_BOARD_CARD_TITLE_REQUIRED: "Dê um título ao card.",
    ERR_BOARD_MOVE_INVALID: "Movimento inválido.",
    ERR_BOARD_COMMENT_EMPTY: "Escreva algo no comentário.",
    ERR_BOARD_LINK_KIND: "Tipo de vínculo inválido.",
    ERR_BOARD_LINK_INVALID: "Vínculo inválido (não encontrado nesta clínica).",
    ERR_BOARD_TICKET_NOT_FOUND: "Conversa não encontrada.",
    ERR_BOARD_PATIENT_NOT_FOUND: "Paciente não encontrado.",
    ERR_BOARD_AUTOMATION_NOT_FOUND: "Automação não encontrada.",
    ERR_BOARD_AUTOMATION_INVALID: "Configure o gatilho e a ação da automação.",
    ERR_BOARD_FILE_REQUIRED: "Selecione um arquivo.",
    ERR_BOARD_FILE_NOT_FOUND: "Anexo não encontrado.",
    ERR_UPLOAD_MIMETYPE: "Tipo de arquivo não permitido."
  };
  if (code && map[code]) return map[code];
  if (code && typeof code === "string" && !code.startsWith("ERR_")) return code;
  return "Não foi possível concluir a operação. Tente novamente.";
};

// ============================ Quadros PRO (S60) ============================
// (usa o `api` já importado no topo)

// Card Pro
export const duplicateCard = async (id) => (await api.post(`/board-cards/${id}/duplicate`)).data;
export const archiveCard = async (id) => (await api.post(`/board-cards/${id}/archive`)).data;
export const restoreCard = async (id) => (await api.post(`/board-cards/${id}/restore`)).data;

// Stats do quadro
export const getBoardStats = async (id) => (await api.get(`/boards/${id}/stats`)).data; // { total, done, overdue, byList, byAssignee }

// Automações (Butler)
export const listAutomations = async (boardId) => (await api.get(`/boards/${boardId}/automations`)).data; // { automations }
export const createAutomation = async (boardId, payload) => (await api.post(`/boards/${boardId}/automations`, payload)).data;
export const updateAutomationRule = async (id, payload) => (await api.put(`/board-automations/${id}`, payload)).data;
export const removeAutomation = async (id) => (await api.delete(`/board-automations/${id}`)).data;

// Notificações (sino)
export const listBoardNotifications = async (onlyUnread = false) =>
  (await api.get("/board-notifications", { params: onlyUnread ? { unread: true } : {} })).data; // { notifications, unread }
export const readBoardNotifications = async (id) =>
  (await api.post(id ? `/board-notifications/${id}/read` : "/board-notifications/read")).data;

// Anexos
export const listCardAttachments = async (cardId) => (await api.get(`/board-cards/${cardId}/attachments`)).data; // { attachments }
export const uploadCardAttachment = async (cardId, file) => {
  const fd = new FormData();
  fd.append("file", file);
  return (await api.post(`/board-cards/${cardId}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } })).data;
};
export const removeCardAttachment = async (id) => (await api.delete(`/board-attachments/${id}`)).data;
export const attachmentDownloadUrl = (id) => `${api.defaults.baseURL || ""}/board-attachments/${id}/download`;

// "virar tarefa" de um paciente
export const createCardFromPatient = async (payload) => (await api.post("/board-link/from-patient", payload)).data;

// ----------------------------- metas/helpers -----------------------------
export const PRIORITIES = [
  { value: "", label: "Sem prioridade", color: "#bdbdbd" },
  { value: "baixa", label: "Baixa", color: "#2e7d32" },
  { value: "media", label: "Média", color: "#f9a825" },
  { value: "alta", label: "Alta", color: "#ef6c00" },
  { value: "urgente", label: "Urgente", color: "#c62828" }
];
export const priorityMeta = (v) => PRIORITIES.find((p) => p.value === (v || "")) || PRIORITIES[0];

export const BOARD_BACKGROUNDS = [
  "#1565c0", "#2e7d32", "#6a1b9a", "#ad1457", "#00838f", "#4e342e", "#37474f", "#283593"
];

// fundos em gradiente (estilo Trello). board.background = cor hex OU key de gradiente.
export const BOARD_GRADIENTS = [
  { key: "grad-blue", css: "linear-gradient(135deg,#1565c0,#42a5f5)" },
  { key: "grad-purple", css: "linear-gradient(135deg,#6a1b9a,#ab47bc)" },
  { key: "grad-green", css: "linear-gradient(135deg,#1b5e20,#66bb6a)" },
  { key: "grad-sunset", css: "linear-gradient(135deg,#e65100,#ffa726)" },
  { key: "grad-pink", css: "linear-gradient(135deg,#ad1457,#ec407a)" },
  { key: "grad-teal", css: "linear-gradient(135deg,#00695c,#26a69a)" },
  { key: "grad-night", css: "linear-gradient(135deg,#1a237e,#3949ab)" },
  { key: "grad-slate", css: "linear-gradient(135deg,#37474f,#78909c)" }
];
// resolve o style do fundo do quadro (cor OU gradiente)
export const boardBgStyle = (background) => {
  if (!background) return {};
  const g = BOARD_GRADIENTS.find((x) => x.key === background);
  if (g) return { backgroundImage: g.css };
  if (String(background).indexOf("#") === 0) return { backgroundColor: background };
  return {};
};

export const BOARD_VIEWS = [
  { value: "board", label: "Quadro" },
  { value: "calendar", label: "Calendário" },
  { value: "table", label: "Tabela" }
];

// Templates clínicos (1-clique). seed: cards na lista por índice.
export const BOARD_TEMPLATES = [
  { key: "blank", name: "Quadro em branco", type: "task", icon: "🗂️", seed: [] },
  { key: "recepcao", name: "Recepção do dia", type: "task", icon: "🛎️", seed: [
    { list: 0, title: "Conferir agenda do dia" },
    { list: 0, title: "Confirmar consultas (WhatsApp)" },
    { list: 0, title: "Organizar prontuários" }
  ] },
  { key: "posop", name: "Pós-operatório", type: "task", icon: "🩹", seed: [
    { list: 0, title: "Ligar 24h após o procedimento" },
    { list: 0, title: "Retorno em 7 dias" },
    { list: 0, title: "Retirada de pontos" }
  ] },
  { key: "leads", name: "Captação de leads", type: "pipeline", icon: "🧲", seed: [] },
  { key: "onboarding", name: "Onboarding de paciente", type: "task", icon: "🌱", seed: [
    { list: 0, title: "Cadastro completo" },
    { list: 0, title: "Anamnese" },
    { list: 0, title: "Termo de consentimento (LGPD)" }
  ] },
  { key: "admin", name: "Tarefas administrativas", type: "task", icon: "📋", seed: [
    { list: 0, title: "Conferir estoque" },
    { list: 0, title: "Pagar contas do mês" },
    { list: 0, title: "Conciliar o caixa" }
  ] }
];

// selo de prazo (Atrasado/Hoje/Em breve) — null se não aplicável
export const dueMeta = (iso, done) => {
  if (!iso || done) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const due = new Date(d); due.setHours(0, 0, 0, 0);
  const diff = Math.round((due.getTime() - today.getTime()) / 86400000);
  if (diff < 0) return { label: `Atrasado ${-diff}d`, color: "#c62828" };
  if (diff === 0) return { label: "Vence hoje", color: "#ef6c00" };
  if (diff <= 3) return { label: `Em ${diff}d`, color: "#f9a825" };
  return null;
};

// progresso do checklist
export const checklistProgress = (checklist) => {
  const arr = Array.isArray(checklist) ? checklist : [];
  const total = arr.length;
  const done = arr.filter((c) => c && c.done).length;
  return { total, done, pct: total ? Math.round((done / total) * 100) : 0 };
};

// R$ 1.234,56 (sem depender de Intl/ICU)
export const fmtMoneyBR = (v) => {
  const n = Number(v) || 0;
  const [int, dec] = Math.abs(n).toFixed(2).split(".");
  return `${n < 0 ? "-" : ""}R$ ${int.replace(/\B(?=(\d{3})+(?!\d))/g, ".")},${dec}`;
};

// LINK_KINDS já exportado acima (Q2). PRO reusa.
